/**
 * Ted 09-24 / 09-29 (lane LC) — "Before you check in", as data.
 *
 * Ted (09-24): at check-in "missing vaccination records, transportation
 * confirmation, medications, Spayed/Neutered status when relevant, and any
 * Do Not Admit warning should be very noticeable. Do Not Admit should be a
 * hard stop." (09-29): "I like check-in being the 'loud' point and checkout
 * staying relatively quiet except for credit balances or balances due."
 *
 * The registry (`dog-flags.ts`) already says WHICH flags alert at check-in
 * (`alertAt`); until now the only interrupting consumer was Quick Book at
 * creation, and every check-in button let the dog through with the pills as
 * decoration. This module turns one booking — or a household's worth — into
 * the plan the dialog renders and the decisions the check-in POST carries:
 *
 *   · ITEMS — every flag alerting at `checkin`, acknowledged ONE BY ONE
 *     (never one blanket yes — Ted #26's "never removing staff control").
 *     The transport item is special: acknowledging it IS the "physically
 *     here" confirmation, so the POST carries `arrival_verified: true` and
 *     the server's 409 never fires. The 409 → `ArrivalVerifyDialog` path
 *     stays as the fallback for a surface that forgot to ask.
 *   · STOPS — a dog carrying `banned`. There is no checkbox: the server
 *     refuses (409 `do_not_admit`) and so does this plan. In a household the
 *     siblings still go in; the stopped dog is named.
 *   · HEAT CHECKS (#1111 AC5) — for every dog the registry's heat rule
 *     applies to (`heatCheckAppliesFromCard`: not known male, not known
 *     spayed/neutered, any age — the reservation gate's rule), the desk answers "Is she
 *     in heat?" Only "No" lets the check-in proceed (`heat_confirmed: true`);
 *     "Yes" stops that dog (we do not accept females in heat) and points at
 *     the mid-stay path if she is already inside. The owner's own
 *     declaration from the reservation (lane LG's column) is shown when the
 *     row carries it — and the desk is still asked.
 *
 * Pure: no React, no fetch. The dialog renders it; the surfaces act on it.
 * Rows that carry no `dogs` (an older server) produce an EMPTY plan — never
 * a guessed interrupt, so the ordinary dog stays one tap (Ted #28 AC).
 */
import { flagsAlertingAt, heatCheckAppliesFromCard } from '@ferro/core/modules/dog-profile/dog-flags.js';
import type { AdminBooking, DogCard, DogFlag } from '../../lib/api.js';

/** The minimum a surface hands the plan — both `DashboardArrival` and `AdminBooking` map onto it. */
export interface InterruptRow {
  readonly bookingId: string;
  readonly customerName: string;
  readonly dogNames: readonly string[];
  /** The dog-first cards with the registry's flags; absent on an older server ⇒ no plan. */
  readonly dogs?: readonly DogCard[] | null;
  /** Lane LG's reservation-time declaration, rendered only when present. */
  readonly heatDeclaration?: 'not_in_heat' | null;
  readonly heatDeclaredAt?: string | null;
}

export interface InterruptItem {
  /** Stable id for the checkbox (`id` + `name`, the #1103 label guard). */
  readonly id: string;
  /** The booking this item belongs to; null for an owner-level (account) item. */
  readonly bookingId: string | null;
  /** The dog the flag is about; null for a stay-level or owner-level item. */
  readonly dogName: string | null;
  /** The dogs on the booking, for a stay-level item's wording. */
  readonly dogNames: readonly string[];
  readonly flag: DogFlag;
  /** Transport's arrival check — acknowledging this IS the "physically here" answer. */
  readonly arrivalConfirm: boolean;
}

export interface InterruptStop {
  readonly bookingId: string;
  readonly dogName: string;
  readonly flag: DogFlag;
}

export interface HeatCheck {
  readonly id: string;
  readonly bookingId: string;
  readonly dogId: string;
  readonly dogName: string;
  /** The owner's declaration, when the row carries one. */
  readonly declaration: { readonly declaredAt: string | null } | null;
}

export interface CheckInInterruptPlan {
  readonly rows: readonly InterruptRow[];
  readonly items: readonly InterruptItem[];
  readonly stops: readonly InterruptStop[];
  readonly heatChecks: readonly HeatCheck[];
  /** False ⇒ the surface checks in without asking (the ordinary dog). */
  readonly needed: boolean;
}

/** The hard stop at the door. Named here so the plan and the server agree on the key. */
export const DO_NOT_ADMIT_KEY = 'banned';

function safeId(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]/g, '_');
}

export function planCheckInInterrupt(rows: ReadonlyArray<InterruptRow>): CheckInInterruptPlan {
  const items: InterruptItem[] = [];
  const stops: InterruptStop[] = [];
  const heatChecks: HeatCheck[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    const dogs = Array.isArray(row.dogs) ? row.dogs : [];
    for (const dog of dogs) {
      const flags: readonly DogFlag[] = Array.isArray(dog.flags) ? dog.flags : [];
      // The stop first — it is not an item, there is nothing to acknowledge.
      const banned = flags.find((f) => f.key === DO_NOT_ADMIT_KEY);
      if (banned) stops.push({ bookingId: row.bookingId, dogName: dog.name, flag: banned });

      for (const flag of flagsAlertingAt<DogFlag>(flags, 'checkin')) {
        if (flag.key === DO_NOT_ADMIT_KEY) continue;
        let id: string;
        let bookingId: string | null = row.bookingId;
        let dogName: string | null = dog.name;
        if (flag.scope === 'account') {
          // The family shares the owner's flags — once per owner, not per dog.
          id = `owner:${safeId(row.customerName)}:${flag.key}`;
          bookingId = null;
          dogName = null;
        } else if (flag.scope === 'stay') {
          // Every dog of a booking carries the stay's flags — once per booking.
          id = `${safeId(row.bookingId)}:stay:${flag.key}`;
          dogName = null;
        } else {
          id = `${safeId(row.bookingId)}:${safeId(dog.dogId)}:${flag.key}`;
        }
        if (seen.has(id)) continue;
        seen.add(id);
        items.push({
          id,
          bookingId,
          dogName,
          dogNames: row.dogNames,
          flag,
          arrivalConfirm: flag.key === 'transport' && flag.requiresArrivalCheck === true,
        });
      }

      // #1111 AC5 — the registry's ONE heat rule (sex + derived altered status).
      if (heatCheckAppliesFromCard({ sex: dog.sex, altered: dog.altered })) {
        heatChecks.push({
          id: `${safeId(row.bookingId)}:${safeId(dog.dogId)}:heat`,
          bookingId: row.bookingId,
          dogId: dog.dogId,
          dogName: dog.name,
          declaration:
            row.heatDeclaration === 'not_in_heat' ? { declaredAt: row.heatDeclaredAt ?? null } : null,
        });
      }
    }
  }

  return {
    rows,
    items,
    stops,
    heatChecks,
    needed: items.length > 0 || stops.length > 0 || heatChecks.length > 0,
  };
}

export type HeatAnswer = 'no' | 'yes';

export interface InterruptState {
  readonly acked: ReadonlySet<string>;
  readonly heat: Readonly<Record<string, HeatAnswer | undefined>>;
}

export interface InterruptDecision {
  readonly bookingId: string;
  /** Send `arrival_verified: true` — the transport item was acknowledged. */
  readonly arrivalVerified: boolean;
  /** Send `heat_confirmed: true` — every heat check on the booking answered No. */
  readonly heatConfirmed: boolean;
}

export interface InterruptStopped {
  readonly bookingId: string;
  readonly dogName: string;
  readonly reason: 'do_not_admit' | 'in_heat';
}

export interface InterruptOutcome {
  readonly proceed: readonly InterruptDecision[];
  readonly stopped: readonly InterruptStopped[];
}

/**
 * What the desk's answers so far mean. `complete` ⇒ every item is acknowledged
 * and every heat check answered; `outcome` is what the POSTs should carry
 * (computed even when incomplete, for the button label).
 */
export function resolveInterrupt(plan: CheckInInterruptPlan, state: InterruptState): { complete: boolean; outcome: InterruptOutcome } {
  const stopped: InterruptStopped[] = plan.stops.map((s) => ({ bookingId: s.bookingId, dogName: s.dogName, reason: 'do_not_admit' as const }));
  for (const h of plan.heatChecks) {
    if (state.heat[h.id] === 'yes') stopped.push({ bookingId: h.bookingId, dogName: h.dogName, reason: 'in_heat' });
  }
  const stoppedIds = new Set(stopped.map((s) => s.bookingId));
  const proceed: InterruptDecision[] = plan.rows
    .filter((r) => !stoppedIds.has(r.bookingId))
    .map((r) => ({
      bookingId: r.bookingId,
      arrivalVerified: plan.items.some((i) => i.bookingId === r.bookingId && i.arrivalConfirm && state.acked.has(i.id)),
      heatConfirmed: plan.heatChecks.some((h) => h.bookingId === r.bookingId && state.heat[h.id] === 'no'),
    }));
  const allAcked = plan.items.every((i) => state.acked.has(i.id));
  const allAnswered = plan.heatChecks.every((h) => state.heat[h.id] !== undefined);
  return { complete: allAcked && allAnswered, outcome: { proceed, stopped } };
}

// ── Checkout — QUIET on purpose ──────────────────────────────────────────────

/**
 * Ted 09-29: "checkout staying relatively quiet except for credit balances
 * or balances due." An ALLOWLIST, not `alertAt: 'checkout'`: the registry
 * also marks transport-home and isolated for checkout, and those stay row
 * decoration here. Nothing else pops.
 */
export const CHECKOUT_INTERRUPT_KEYS: ReadonlyArray<string> = ['credit', 'visit_unpaid', 'balance'];

export interface CheckoutInterruptPlan {
  readonly rows: readonly InterruptRow[];
  readonly items: readonly InterruptItem[];
  readonly needed: boolean;
}

export function planCheckoutInterrupt(rows: ReadonlyArray<InterruptRow>): CheckoutInterruptPlan {
  const items: InterruptItem[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const dogs = Array.isArray(row.dogs) ? row.dogs : [];
    for (const dog of dogs) {
      const flags: readonly DogFlag[] = Array.isArray(dog.flags) ? dog.flags : [];
      for (const flag of flagsAlertingAt<DogFlag>(flags, 'checkout')) {
        if (!CHECKOUT_INTERRUPT_KEYS.includes(flag.key)) continue;
        // `visit_unpaid` is THIS visit's money (once per booking); `balance`
        // and `credit` are the owner's (once per owner).
        const id =
          flag.key === 'visit_unpaid'
            ? `${safeId(row.bookingId)}:${flag.key}`
            : `owner:${safeId(row.customerName)}:${flag.key}`;
        if (seen.has(id)) continue;
        seen.add(id);
        items.push({
          id,
          bookingId: flag.key === 'visit_unpaid' ? row.bookingId : null,
          dogName: null,
          dogNames: row.dogNames,
          flag,
          arrivalConfirm: false,
        });
      }
    }
  }
  return { rows, items, needed: items.length > 0 };
}

/** The Bookings list's row (snake_case wire type) as an interrupt row. `DashboardArrival` already fits structurally. */
export function interruptRowFromAdminBooking(b: AdminBooking): InterruptRow {
  return {
    bookingId: b.id,
    customerName: b.customer_name ?? '',
    dogNames: b.dog_names ?? [],
    dogs: b.dogs ?? null,
    heatDeclaration: b.heat_declaration ?? null,
    heatDeclaredAt: b.heat_declared_at ?? null,
  };
}

/** The banner line for the dogs an interrupt stopped — the situation, never the mechanism. */
export function describeStopped(stopped: ReadonlyArray<InterruptStopped>): string | null {
  if (stopped.length === 0) return null;
  return `Not checked in — ${stopped
    .map((s) => `${s.dogName}: ${s.reason === 'do_not_admit' ? 'Do Not Admit' : 'in heat — not admitted today'}`)
    .join(' · ')}`;
}

/** "Hazel" · "Hazel & Hank" · "Hazel, Hank & Skipper" — the household helper's wording. */
export function joinNames(names: ReadonlyArray<string>): string {
  if (names.length === 0) return 'this dog';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}
